import { useEffect, useMemo, useRef, useState } from 'react'

import { Heading, Text, theme } from '@hhgtech/hhg-components'
import { ChartOptions } from 'chart.js'
import { Chart as ChartJS } from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { gsap } from 'gsap'
import throttle from 'lodash/throttle'
import { Chart } from 'react-chartjs-2'
import { useIntl } from 'react-intl'

import {
  StyledBarChart,
  StyledChartContainer,
  StyledChartLegend,
  StyledContainer,
  StyledLegendBox,
} from './index.styled'
import 'chart.js/auto'

ChartJS.register(ChartDataLabels)

type HealthChartData = {
  datasets: {
    label: string
    data: number[]
    color: string
    borderRadius?: number
    barPercentage?: number
    barThickness?: number
    categoryPercentage?: number
  }[]
  dataLabels: string[]
}

type Props = {
  isMobile: boolean
  title?: string
  description?: string
}

const AnimationChart = ({ isMobile, title, description }: Props) => {
  const [hasScrollToView, setHasScrollToView] = useState(false)
  const { formatMessage: f } = useIntl()
  const viewContaier = useRef<HTMLDivElement>(null)
  const el = useRef(null)
  const q = gsap.utils.selector(el)
  const tl = useRef({})

  const healthChartData: HealthChartData = useMemo(
    () => ({
      datasets: [
        {
          label: f({
            id: 'superContent.chart.healthTool.labels.lifeStyle',
            defaultMessage: 'Living Healthy Lifestyles',
          }),
          data: [90, 80, 76, 86, 74, 74],
          color: '#91CAFF',
        },
        {
          label: f({
            id: 'superContent.chart.healthTool.labels.diseasePrevention',
            defaultMessage: 'Disease Prevention',
          }),
          data: [64, 64, 62, 56, 53, 61],
          color: '#2D87F3',
        },
        {
          label: f({
            id: 'superContent.chart.healthTool.labels.diseaseManagement',
            defaultMessage: 'Disease treatment and management',
          }),
          data: [56, 0, 60, 48, 52, 53],
          color: '#FFB521',
        },
      ],
      dataLabels: [
        f({
          id: 'superContent.chart.healthTool.items.indonesia',
          defaultMessage: 'Indonesia',
        }),
        f({
          id: 'superContent.chart.healthTool.items.malaysia',
          defaultMessage: 'Malaysia',
        }),
        f({
          id: 'superContent.chart.healthTool.items.thailand',
          defaultMessage: 'Thailand',
        }),
        f({
          id: 'superContent.chart.healthTool.items.myanmar',
          defaultMessage: 'Myanmar',
        }),
        f({
          id: 'superContent.chart.healthTool.items.taiwan',
          defaultMessage: 'Taiwan',
        }),
        f({
          id: 'superContent.chart.healthTool.items.india',
          defaultMessage: 'India',
        }),
      ],
    }),
    [],
  )

  useEffect(() => {
    const handleScroll = throttle(() => {
      if (!hasScrollToView && typeof window !== 'undefined') {
        const winScroll =
          document.body.scrollTop || document.documentElement.scrollTop

        if (
          winScroll -
            (viewContaier?.current?.offsetTop || 0) +
            window.innerHeight -
            100 >
          0
        ) {
          setHasScrollToView(true)
          window.removeEventListener('scroll', handleScroll)
        }
      }
    }, 200)

    if (
      (viewContaier?.current?.offsetTop || 0) <
      (typeof window !== 'undefined' ? window.innerHeight : 1000)
    ) {
      setHasScrollToView(true)
    } else if (typeof window !== 'undefined') {
      window.addEventListener('scroll', handleScroll, { passive: true })
      return () => {
        window.removeEventListener('scroll', handleScroll)
      }
    }
  }, [])

  useEffect(() => {
    if (hasScrollToView) {
      tl.current = gsap
        .timeline()
        .fromTo(
          q('.heading'),
          {
            y: 40,
            opacity: 0,
          },
          {
            y: 0,
            opacity: 1,
            duration: 0.8,
          },
        )
        .fromTo(
          q('.description'),
          {
            y: 40,
            opacity: 0,
          },
          {
            y: 0,
            opacity: 1,
            duration: 0.5,
          },
        )
        .from(
          q('.legend'),
          {
            x: 100,
            duration: 0.8,
            opacity: 0,
          },
          0.5,
        )
    }
  }, [hasScrollToView])

  const dataset = useMemo(
    () => ({
      labels: healthChartData?.dataLabels,
      datasets: healthChartData?.datasets?.map((category) => ({
        borderRadius: Number.MAX_VALUE,
        barPercentage: 1.0,
        barThickness: 8,
        categoryPercentage: 1.0,
        ...category,
        backgroundColor: category?.color,
      })),
    }),
    [],
  )

  const options: ChartOptions = useMemo(
    () => ({
      indexAxis: 'y',
      elements: {
        bar: {
          borderWidth: 0,
        },
      },
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        xAxis: {
          ...(isMobile ? {} : { suggestedMax: 100 }),
          grid: {
            display: !isMobile,
            borderDash: [5, 5],
            borderDashOffset: 2,
            color: function (context) {
              if (context.tick.value === 0) {
                return 'rgba(0, 0, 0, 0)'
              }
              return 'rgba(0, 0, 0, 0.1)'
            },
          },
          ticks: {
            display: !isMobile,
            callback: (tickValue) => {
              return Number(tickValue) > 0 ? `${tickValue}%` : ''
            },
            padding: -390,
          },
        },
        yAxis: {
          grid: {
            display: false,
          },
          ticks: {
            crossAlign: 'far',
            font: {
              weight: '600',
              size: isMobile ? 12 : 16,
            },
          },
        },
      },

      plugins: {
        legend: {
          display: false,
        },
        datalabels: {
          anchor: 'end',
          offset: 4,
          align: 'end',
          color: (ctx) => {
            return (ctx?.dataset?.backgroundColor as string) || 'blue'
          },
          font: {
            weight: 'bold',
            size: 8,
          },
          formatter: (value) => {
            return value ? `${value}%` : ''
          },
        },
        tooltip: {
          enabled: false,
        },
      },
      layout: {
        ...(isMobile
          ? {}
          : {
              padding: {
                top: 32,
                right: 32,
              },
            }),
      },
      animation: {
        duration: 1000,
        easing: 'easeInQuint',
      },
    }),
    [isMobile],
  )

  return (
    <StyledContainer ref={el}>
      {title ? (
        <Heading
          className="heading"
          tag="h3"
          color={theme.colors.primaryActive}
        >
          {title}
        </Heading>
      ) : null}

      {description ? (
        <Text size="p4" className="description">
          {description}
        </Text>
      ) : null}

      <StyledChartContainer ref={viewContaier}>
        {hasScrollToView ? (
          <>
            <StyledBarChart>
              {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
              <Chart
                type="bar"
                data={dataset as any}
                options={options as any}
              />
            </StyledBarChart>
            <StyledChartLegend>
              {healthChartData?.datasets?.map((category, index) => (
                <div className="legend" key={index}>
                  <StyledLegendBox
                    style={{ backgroundColor: category?.color }}
                  />
                  <div className="legend-label">{category?.label}</div>
                </div>
              ))}
            </StyledChartLegend>
          </>
        ) : null}
      </StyledChartContainer>
    </StyledContainer>
  )
}

export { AnimationChart }
